import { Grid, Stack, Title } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';

import { TransitGroup } from '@e-origin/shared';

import { MultiLangCodes, TextInput } from '@components';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { TransitViewStakeholdersFormValues } from './validateTransitViewStakeholdersForm';

interface StakeholderSectionProps {
  form: UseFormReturnType<TransitViewStakeholdersFormValues>;
  editDisabled: boolean;
  type: 'Consignor' | 'Consignee' | 'HolderOfTheTransitProcedure';
}

export const StakeholderSection: React.FC<StakeholderSectionProps> = ({ form, editDisabled, type }) => {
  const isArrival = form.values.generalInfo?.group === TransitGroup.ARRIVAL;
  const codelistSystems = useCodeListSystem({ isTransit: true });

  const getTitle = () => {
    return type === 'HolderOfTheTransitProcedure' ? 'Holder of the transit procedure' : type;
  };

  const getBasePath = () => {
    if (type === 'Consignor' || type === 'Consignee') {
      return `Consignment.${type}`;
    }
    return type;
  };

  const basePath = getBasePath();
  const showContactPerson = !isArrival && (type === 'Consignor' || type === 'HolderOfTheTransitProcedure');

  const renderContactPerson = () => {
    if (!showContactPerson) return null;

    return (
      <Grid>
        <Grid.Col span={3}>
          <TextInput {...form.getInputProps(`${basePath}.ContactPerson.name`)} label="Contact Name" disabled={editDisabled} />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput {...form.getInputProps(`${basePath}.ContactPerson.phoneNumber`)} label="Phone Number" disabled={editDisabled} />
        </Grid.Col>
        <Grid.Col span={3}>
          <TextInput {...form.getInputProps(`${basePath}.ContactPerson.eMailAddress`)} label="eMail Address" disabled={editDisabled} />
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <Stack mb="xl">
      <Title order={5} fw={600}>
        {getTitle()}
      </Title>

      <Stack>
        <Grid>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps(`${basePath}.identificationNumber`)} label="Identification number" disabled={editDisabled} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput {...form.getInputProps(`${basePath}.name`)} label="Name" disabled={editDisabled} />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput
              data-pw={`${type.toLowerCase()}-street-input`}
              {...form.getInputProps(`${basePath}.Address.streetAndNumber`)}
              label="Street and Number"
              disabled={editDisabled}
            />
          </Grid.Col>
        </Grid>

        <Grid>
          <Grid.Col span={3}>
            <TextInput
              data-pw={`${type.toLowerCase()}-postcode-input`}
              {...form.getInputProps(`${basePath}.Address.postcode`)}
              label="Postcode"
              disabled={editDisabled}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <TextInput data-pw={`${type.toLowerCase()}-city-input`} {...form.getInputProps(`${basePath}.Address.city`)} label="City" disabled={editDisabled} />
          </Grid.Col>
          <Grid.Col span={3}>
            <MultiLangCodes
              data-pw={`${type.toLowerCase()}-country-input`}
              {...form.getInputProps(`${basePath}.Address.country`)}
              label="Country"
              system={codelistSystems}
              code="CountryCodesCommonTransit"
              disabled={editDisabled}
            />
          </Grid.Col>
        </Grid>

        {renderContactPerson()}
      </Stack>
    </Stack>
  );
};
